import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../../store";
import { APP_STORE_URL, PLAY_STORE_URL } from "../../../utils/constants";

const STORAGE_KEY = "appPromptHiddenUntil";
const DAY = 24 * 60 * 60 * 1000;
const HIDE_AFTER_CONTINUE = 7 * DAY;
const HIDE_AFTER_DOWNLOAD = 30 * DAY;

const PLATFORMS = {
  ios: { device: "iPhone", cta: "App Store'dan İndir", url: APP_STORE_URL, icon: "fa-apple" },
  android: { device: "Android", cta: "Google Play'den İndir", url: PLAY_STORE_URL, icon: "fa-play" },
};

const detectPlatform = () => {
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  // iPadOS 13+ masaüstü Safari kimliğiyle gelir; dokunmatik Mac olarak ayırt edilir.
  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) {
    return "ios";
  }
  return null;
};

const isStandalone = () =>
  window.navigator.standalone === true ||
  (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);

const isHidden = () => {
  try {
    return Number(localStorage.getItem(STORAGE_KEY) || 0) > Date.now();
  } catch (e) {
    return false;
  }
};

const hideFor = (ms) => {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now() + ms));
  } catch (e) {}
};

const track = (action, platform) => {
  if (typeof window.gtag === "function") {
    window.gtag("event", "app_download_prompt", { prompt_action: action, platform });
  }
};

// Mobil tarayıcıdan giriş yapmış kullanıcılara uygulamayı tanıtır; web'den devam etme seçeneği sunar.
// Yalnızca giriş yapanlara gösterildiği için arama motoru botlarını (mobil ara reklam cezası) etkilemez.
const AppDownloadPrompt = () => {
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const [platform, setPlatform] = useState(null);
  const primaryRef = useRef(null);

  const close = useCallback(
    (action, hideMs) => {
      hideFor(hideMs);
      track(action, platform);
      setPlatform(null);
    },
    [platform]
  );
  const continueOnWeb = useCallback(() => close("continue_web", HIDE_AFTER_CONTINUE), [close]);
  const onDownload = () => close("download", HIDE_AFTER_DOWNLOAD);

  useEffect(() => {
    if (!currentUser?.id || isStandalone() || isHidden()) return;
    const detected = detectPlatform();
    if (!detected) return;
    const timer = setTimeout(() => {
      setPlatform(detected);
      track("shown", detected);
    }, 1200);
    return () => clearTimeout(timer);
  }, [currentUser?.id]);

  useEffect(() => {
    if (!platform) return;
    primaryRef.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === "Escape") continueOnWeb();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [platform, continueOnWeb]);

  if (!platform) return null;
  const info = PLATFORMS[platform];

  return (
    <div
      onClick={continueOnWeb}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        backgroundColor: "rgba(5, 7, 12, 0.6)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-prompt-title"
        aria-describedby="app-prompt-desc"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 480,
          backgroundColor: "#1c202b",
          color: "#ffffff",
          borderRadius: "20px 20px 0 0",
          padding: "24px 20px calc(20px + env(safe-area-inset-bottom))",
          boxShadow: "0 -12px 32px rgba(0, 0, 0, 0.45)",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            margin: "0 auto 14px",
            borderRadius: "50%",
            backgroundColor: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img src="/images/logo.png" alt="" width={60} height={60} />
        </div>
        <h2 id="app-prompt-title" style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px", color: "#ffffff" }}>
          muzisyen.app artık cebinde
        </h2>
        <p id="app-prompt-desc" style={{ fontSize: 14, lineHeight: 1.5, color: "#c3c8d1", margin: "0 0 20px" }}>
          {info.device} uygulamamızla bildirimleri anında al, mesajlarına hızlıca cevap ver ve
          ilanlarını her yerden yönet.
        </p>
        <a
          ref={primaryRef}
          href={info.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onDownload}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            width: "100%",
            minHeight: 48,
            borderRadius: 24,
            backgroundColor: "#02b875",
            color: "#ffffff",
            fontSize: 16,
            fontWeight: 700,
            textDecoration: "none",
            marginBottom: 10,
          }}
        >
          <i className={`fa ${info.icon}`} aria-hidden="true" style={{ fontSize: 20 }} />
          {info.cta}
        </a>
        <button
          type="button"
          onClick={continueOnWeb}
          style={{
            width: "100%",
            minHeight: 44,
            border: "none",
            borderRadius: 24,
            backgroundColor: "transparent",
            color: "#c3c8d1",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Web'den devam et
        </button>
      </div>
    </div>
  );
};

export default AppDownloadPrompt;
